/**
 * KPI Card Component
 * Displays a key performance indicator with icon and trend
 */

import { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '@/core/components';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  onClick?: () => void;
  className?: string;
}

export function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  onClick,
  className = '',
}: KPICardProps) {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      case 'neutral':
        return <Minus className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      case 'neutral':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const cardContent = (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        {subtitle && (
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        )}
        {(trend ?? trendValue) && (
          <div className="mt-2 flex items-center gap-1">
            {trend && getTrendIcon()}
            {trendValue && (
              <span className={`text-sm font-medium ${getTrendColor()}`}>
                {trendValue}
              </span>
            )}
          </div>
        )}
      </div>
      {icon && (
        <div className="flex-shrink-0 ml-4">
          {icon}
        </div>
      )}
    </div>
  );

  if (onClick) {
    return (
      <div onClick={onClick} className={className}>
        <Card
          padding="md"
          className="cursor-pointer hover:shadow-lg transition-shadow"
        >
          {cardContent}
        </Card>
      </div>
    );
  }

  return (
    <Card padding="md" className={className}>
      {cardContent}
    </Card>
  );
}
